import Logo from 'assets/img/header.svg?react';
import { useFormik } from 'formik';
import BaseButton from 'components/atoms/baseButton';
import BaseLoader from 'components/atoms/base-loader';
import { fullNamePresenter } from 'utils';
import { DateFormatter } from 'infrastructure/functions';
import {
  useSendCampaignOutcomeData,
  useGetCampaignOutcomeData,
} from 'infrastructure/hooks/sms-campaigns';
import ErrorImg from 'assets/img/error.svg?react';
import { CcmStatuses, Languages } from 'infrastructure/enums';
import i18n from 'infrastructure/i18n';
import { showResult } from 'infrastructure/hooks/utils';

import s from './styles.module.scss';
import OutcomeForm, { titlesMap } from './form';
import SuccessMessage from './success-message';
import AlreadySentMessage from './already-sent-message';

import type { ISmsCampaignOutcomeData } from 'infrastructure/hooks/sms-campaigns';
import type {
  IOutcomesFormValues,
  TQuestionGroup,
} from 'infrastructure/interfaces';
import type { FC } from 'react';

const submitBtnLabelMap = {
  en: i18n.t('smsCampaign.sendFormEng'),
  es: i18n.t('smsCampaign.sendFormEsp'),
} as const;

const CampaignForm: FC = () => {
  const searchParams = new URLSearchParams(window.location.search);
  const token = searchParams.get('token');
  const { data, isLoading, isError } = useGetCampaignOutcomeData(token);
  const { sendCampaignOutcome, isSendLoading, sendData, isSendError } =
    useSendCampaignOutcomeData();

  const patientLanguage = data?.patient.language === Languages.Es ? 'es' : 'en';

  const initialValues: IOutcomesFormValues = data
    ? data.questionAnswers.reduce(
        (acc, item) => ({
          ...acc,
          [item.guid]: { answers: [], anyAnswer: '', otherAnswerGuid: null },
        }),
        {},
      )
    : {};

  const formik = useFormik<IOutcomesFormValues>({
    initialValues,
    onSubmit: async (postData) => {
      if (!token) return;

      const outcomeData: ISmsCampaignOutcomeData[] = Object.entries(
        postData,
      ).map(([key, value]) => {
        const anyAnswer = value?.anyAnswer || null;
        const otherAnswerGuid = value?.otherAnswerGuid ?? null;
        const answers =
          value?.answers.filter(
            (answersGuid) => answersGuid !== otherAnswerGuid,
          ) ?? [];

        return {
          questionGuid: key,
          answers,
          anyAnswer,
          otherAnswerGuid,
        };
      });

      sendCampaignOutcome(
        {
          data: outcomeData,
          token,
        },
        {
          onSuccess: () => {
            formik.resetForm();
            showResult();
          },
        },
      );
    },
    enableReinitialize: true,
    validateOnChange: false,
    validate(values) {
      const errorText = titlesMap.error[patientLanguage];
      const errors: Record<string, string> = {};

      Object.keys(values).forEach((key) => {
        const isRequiredQuestion = data?.questionAnswers.find(
          (item) => item.guid === key,
        )?.required;

        if (isRequiredQuestion) {
          if (!values[key]?.answers.length) {
            errors[key] = errorText;
          }
        }
      });
      return errors;
    },
  });

  const handleSubmit = async () => {
    await formik.submitForm();
  };

  const showSuccessMessage =
    !isSendLoading && !isSendError && sendData?.message;
  const isAlreadyAnswered =
    !isLoading && !isError && data?.alreadyAnswered && !showSuccessMessage;
  const showSubmitButton =
    !isLoading && !isError && !isAlreadyAnswered && !showSuccessMessage;
  const showError = (!isLoading && isError) || (!isSendLoading && isSendError);
  const showForm =
    !isLoading &&
    !isError &&
    !isAlreadyAnswered &&
    data &&
    data.questionAnswers.length > 0 &&
    !showSuccessMessage;
  const questionGroupName: TQuestionGroup =
    data?.patient.ccmStatus === CcmStatuses.Enrolled ? 'enrolled' : 'other';

  return (
    <div className={s.wrapper}>
      <div className={s.header}>
        <Logo width={103} height={40} />
      </div>
      <div className={s.body}>
        <div className={s['form-wrapper']}>
          {showError && <ErrorImg width="100%" height={500} />}
          {isLoading && (
            <BaseLoader loading height={50} width={50} className={s.loader} />
          )}
          {showForm && (
            <OutcomeForm
              data={data.questionAnswers}
              patientLanguage={patientLanguage}
              formik={formik}
              patientName={fullNamePresenter(data.patient)}
              patientDob={DateFormatter({ date: data.patient.birthDate })}
              patientEmail={data.patient.email}
              groupName={questionGroupName}
            />
          )}

          {isAlreadyAnswered && (
            <p className={s['already-answered']}>
              <AlreadySentMessage lang={patientLanguage} />
            </p>
          )}
          {showSuccessMessage && (
            <p className={s['already-answered']}>
              <SuccessMessage lang={patientLanguage} />
            </p>
          )}
          {showSubmitButton && (
            <BaseButton
              type="primary"
              label={submitBtnLabelMap[patientLanguage]}
              loading={isSendLoading}
              height={40}
              onClick={handleSubmit}
              className={s['submit-button']}
            />
          )}
        </div>
      </div>
    </div>
  );
};
export default CampaignForm;
